import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Field from '../../components/Field';
import { CreditCard, Plus, Trash2, Clock, RefreshCw, AlertTriangle, Users, Percent } from 'lucide-react';

const API_URL = `${process.env.REACT_APP_API_URL || 'http://localhost:8000'}/api/v1/public/planes/`;

const EMPTY = {
  id: null, name: '', description: '', price: '0',
  max_users: '5', max_products: '500', currency: 'PYG',
  tipo: 'mensual', trial_dias: '15', descuento_anual: '0', valido_hasta: '',
};

// Fecha en formato local a partir de un AAAA-MM-DD, sin pasar por Date():
// `new Date('2027-06-30')` se interpreta en UTC y en Paraguay muestra el día
// anterior.
function fechaLegible(iso) {
  if (!iso) return '';
  const [a, m, d] = iso.split('-');
  return `${d}/${m}/${a}`;
}

function generarDescripcion({ trial_dias, max_users, max_products, name, tipo, valido_hasta }) {
  const u = `Hasta ${max_users} usuarios`;
  const p = `${parseInt(max_products).toLocaleString('es-PY')} productos`;
  if (tipo === 'trial' && valido_hasta) {
    return `Acceso sin costo hasta el ${fechaLegible(valido_hasta)}. ${u} y ${p}.`;
  }
  if (tipo === 'trial') return `Probá el sistema ${trial_dias} días sin cargo. ${u} y ${p}.`;
  const extras = {
    'Básico': 'Ideal para ferreterías pequeñas.',
    'Profesional': 'Para ferreterías en crecimiento.',
    'Premium': 'Para ferreterías grandes.',
  };
  const extra = extras[name] || '';
  return `${u} y ${p}.${extra ? ' ' + extra : ''}`;
}

export default function PlansManager() {
  const [planes, setPlanes] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [formData, setFormData] = useState(EMPTY);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);


  const fetchPlanes = async () => {
    try {
      const res = await axios.get(API_URL);
      setPlanes(res.data);
    } catch {
      setError('Error al cargar los planes.');
    }
  };

  useEffect(() => { fetchPlanes(); }, []);

  const handleOpenModal = (plan = null) => {
    if (plan) {
      setFormData({
        id: plan.id,
        name: plan.name,
        description: plan.description || '',
        price: plan.price,
        max_users: plan.max_users,
        max_products: plan.max_products,
        currency: plan.currency || 'PYG',
        tipo: plan.tipo || 'mensual',
        trial_dias: plan.trial_dias ?? '15',
        descuento_anual: plan.descuento_anual ?? '0',
        valido_hasta: plan.valido_hasta || '',
        tenant_count: plan.tenant_count || 0,
      });
    } else {
      const init = { ...EMPTY };
      init.description = generarDescripcion(init);
      setFormData(init);
    }
    setError(null);
    setIsModalOpen(true);
  };

  const set = (key, value) => setFormData(prev => {
    const next = { ...prev, [key]: value };
    if (key !== 'description') {
      const autoDesc = generarDescripcion(prev);
      if (prev.description === autoDesc || prev.description === '') {
        next.description = generarDescripcion(next);
      }
    }
    return next;
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const payload = { ...formData };
      if (payload.tipo !== 'trial') payload.trial_dias = 0;
      // El campo de fecha vacío llega como cadena, y el backend espera
      // una fecha o nulo.
      payload.valido_hasta = payload.valido_hasta || null;
      if (formData.id) {
        await axios.put(`${API_URL}${formData.id}/`, payload);
      } else {
        payload.tipo = 'mensual';
        await axios.post(API_URL, payload);
      }
      setIsModalOpen(false);
      fetchPlanes();
    } catch {
      setError('Error al guardar el plan. Verificá que el nombre no esté duplicado.');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('¿Eliminar este plan? Los tenants asignados quedarán sin plan.')) return;
    try {
      await axios.delete(`${API_URL}${id}/`);
      fetchPlanes();
    } catch {
      alert('No se puede eliminar: el plan está en uso por alguna ferretería.');
    }
  };

  const esTrial = (plan) => plan.tipo === 'trial';

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center bg-white p-6 rounded-[2.5rem] shadow-sm border border-slate-100">
        <div className="flex items-center gap-4">
          <div className="bg-brand-50 p-4 rounded-2xl text-brand-800"><CreditCard size={24} /></div>
          <div>
            <h2 className="titulo-modal">Planes de Suscripción</h2>
            <p className="text-[10px] text-slate-500 font-bold uppercase tracking-widest">
              {planes.length} plan{planes.length !== 1 ? 'es' : ''} configurado{planes.length !== 1 ? 's' : ''}
            </p>
          </div>
        </div>
        <button
          onClick={() => handleOpenModal()}
          className="bg-slate-900 text-white px-5 py-2.5 rounded-xl font-black text-xs uppercase tracking-widest hover:bg-slate-800 shadow-lg flex items-center gap-2"
        >
          <Plus size={18} /> Crear Plan
        </button>
      </div>

      {/* Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {planes.map(plan => (
          <div key={plan.id} className="bg-white rounded-[2.5rem] shadow-sm border border-slate-100 p-8 flex flex-col justify-between hover:border-brand-500/30 transition-all">
            <div>
              <div className="flex justify-between items-start mb-6">
                {esTrial(plan) ? (
                  <span className="px-3 py-1 rounded-full text-[10px] font-black uppercase tracking-widest border flex items-center gap-1.5 bg-alerta-50 text-alerta-600 border-alerta-100">
                    <Clock size={11} /> Prueba Gratuita
                  </span>
                ) : (
                  <span className="px-3 py-1 rounded-full text-[10px] font-black uppercase tracking-widest border flex items-center gap-1.5 bg-brand-50 text-brand-800 border-brand-100">
                    <RefreshCw size={11} /> Plan Mensual
                  </span>
                )}
                {!esTrial(plan) && parseFloat(plan.descuento_anual) > 0 && (
                  <span className="text-[10px] font-black text-brand-800 bg-brand-50 px-2 py-1 rounded-full border border-brand-200 flex items-center gap-1">
                    <Percent size={9} /> {plan.descuento_anual}% anual
                  </span>
                )}
                {esTrial(plan) && plan.trial_dias > 0 && (
                  <span className="text-[10px] font-black text-alerta-500 bg-alerta-50 px-2 py-1 rounded-full border border-alerta-100">
                    {plan.trial_dias} días
                  </span>
                )}
              </div>

              <h3 className="text-xl font-black text-slate-800 mb-1 truncate">{plan.name}</h3>
              <p className="text-slate-500 text-sm mb-4 line-clamp-2">{plan.description}</p>

              {esTrial(plan) ? (
                <p className="text-sm font-bold text-alerta-500 mb-4">
                  {plan.valido_hasta
                    ? `Acceso sin costo hasta el ${fechaLegible(plan.valido_hasta)}`
                    : `Acceso gratuito por ${plan.trial_dias} días`}
                </p>
              ) : (
                <div className="mb-4">
                  <p className="text-2xl font-black text-slate-800">
                    {plan.currency === 'PYG'
                      ? `Gs. ${parseFloat(plan.price).toLocaleString('es-PY')}`
                      : `USD ${parseFloat(plan.price).toFixed(2)}`}
                    <span className="text-sm font-medium text-slate-400">/mes</span>
                  </p>
                  {parseFloat(plan.descuento_anual) > 0 && (
                    <p className="text-xs text-brand-800 mt-0.5">
                      Anual: Gs. {Math.round(parseFloat(plan.price) * 12 * (1 - parseFloat(plan.descuento_anual) / 100)).toLocaleString('es-PY')}/año
                    </p>
                  )}
                </div>
              )}

              <div className="flex flex-wrap gap-2 text-xs font-black text-slate-400 uppercase tracking-widest">
                <span className="px-2 py-1 bg-slate-50 rounded-lg">{plan.max_users} usuarios</span>
                <span className="px-2 py-1 bg-slate-50 rounded-lg">{parseInt(plan.max_products).toLocaleString('es-PY')} artículos</span>
              </div>

              {plan.tenant_count > 0 && (
                <div className="flex items-center gap-1.5 mt-3 text-[10px] font-bold text-slate-400">
                  <Users size={11} />
                  <span>{plan.tenant_count} ferretería{plan.tenant_count !== 1 ? 's' : ''} activa{plan.tenant_count !== 1 ? 's' : ''}</span>
                </div>
              )}
            </div>

            <div className="flex gap-3 pt-6 mt-6 border-t border-slate-100">
              <button
                onClick={() => handleOpenModal(plan)}
                className="flex-1 py-3 bg-slate-50 hover:bg-slate-100 rounded-xl text-xs font-black uppercase text-slate-600 tracking-widest transition-all"
              >
                Editar
              </button>
              <button onClick={() => handleDelete(plan.id)} className="p-3 text-slate-300 hover:text-error-500 transition-colors">
                <Trash2 size={18} />
              </button>
            </div>
          </div>
        ))}
      </div>

      {/* Modal */}
      {isModalOpen && (
        <div className="fondo-modal">
          <div className="bg-white rounded-[2.5rem] w-full max-w-sm shadow-2xl p-8 max-h-[90vh] overflow-y-auto">
            <h3 className="text-2xl font-black text-slate-800 tracking-tight mb-6">
              {formData.id ? 'Editar Plan' : 'Nuevo Plan'}
            </h3>

            {error && (
              <div className="aviso-error mb-4">{error}</div>
            )}

            {formData.id && formData.tenant_count > 0 && (
              <div className="mb-4 flex items-start gap-3 bg-alerta-50 border border-alerta-200 rounded-xl p-3">
                <AlertTriangle size={16} className="text-alerta-500 shrink-0 mt-0.5" />
                <p className="text-xs font-bold text-alerta-700">
                  <span className="text-alerta-800">{formData.tenant_count} ferretería{formData.tenant_count !== 1 ? 's' : ''}</span>{' '}
                  usa{formData.tenant_count === 1 ? '' : 'n'} este plan. Los cambios se aplican de inmediato.
                </p>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-4">
              <Field label="Nombre del Plan">
                <input
                  required type="text" className={INPUT_CLS}
                  placeholder="Ej: Plan Básico"
                  value={formData.name}
                  onChange={e => set('name', e.target.value)}
                />
              </Field>

              {formData.tipo === 'trial' ? (
                <>
                  <div className="bg-alerta-50 border border-alerta-200 rounded-xl px-4 py-3 text-xs font-bold text-alerta-700">
                    Plan sin cargo — se edita la duración del acceso, no el precio.
                  </div>
                  <Field label="Válido hasta (fecha fija, opcional)">
                    <input
                      type="date"
                      className={INPUT_CLS}
                      value={formData.valido_hasta}
                      onChange={e => set('valido_hasta', e.target.value)}
                    />
                    <p className="text-xs text-slate-400 mt-1.5">
                      El acceso termina ese día para todos los negocios del plan,
                      sin importar cuándo se dio de alta cada uno. Es lo que usa el
                      plan del piloto. Si lo dejás vacío, se cuentan los días de abajo
                      desde el alta de cada negocio.
                    </p>
                  </Field>
                  {!formData.valido_hasta && (
                    <Field label="Días de acceso gratuito" htmlFor="plan-dias">
                      <div className="relative">
                        <input id="plan-dias"
                          required type="number" min="1" max="365"
                          className={INPUT_CLS}
                          value={formData.trial_dias}
                          onChange={e => set('trial_dias', e.target.value)}
                        />
                        <span className="absolute right-4 top-1/2 -translate-y-1/2 text-xs text-slate-400 font-bold">días</span>
                      </div>
                    </Field>
                  )}
                </>
              ) : (
                <>
                  <Field label="Precio mensual (Gs.)" htmlFor="plan-precio">
                    <div className="relative">
                      <span className="absolute left-4 top-1/2 -translate-y-1/2 text-xs text-slate-400 font-bold">Gs.</span>
                      <input id="plan-precio"
                        required type="number" min="0"
                        className={`${INPUT_CLS} pl-10`}
                        value={formData.price}
                        onChange={e => set('price', e.target.value)}
                      />
                    </div>
                  </Field>

                  <Field label="Descuento anual (% sobre precio × 12)" htmlFor="plan-descuento">
                    <div className="relative">
                      <input id="plan-descuento"
                        type="number" min="0" max="100" step="0.5"
                        className={INPUT_CLS}
                        placeholder="0 = sin opción anual"
                        value={formData.descuento_anual}
                        onChange={e => set('descuento_anual', e.target.value)}
                      />
                      <span className="absolute right-4 top-1/2 -translate-y-1/2 text-xs text-slate-400 font-bold">%</span>
                    </div>
                    <p className="text-[10px] text-slate-400 mt-1 px-1">
                      Con 0% el plan no aparece en la opción anual del landing.
                    </p>
                  </Field>

                  {parseFloat(formData.descuento_anual) > 0 && parseFloat(formData.price) > 0 && (
                    <div className="bg-brand-50 border border-brand-200 rounded-xl px-4 py-3">
                      <p className="text-xs text-slate-500">El cliente verá en el landing:</p>
                      <p className="font-black text-brand-800 text-lg mt-0.5">
                        Gs. {Math.round(parseFloat(formData.price) * 12 * (1 - parseFloat(formData.descuento_anual) / 100)).toLocaleString('es-PY')}
                        <span className="text-sm font-medium text-slate-400">/año</span>
                      </p>
                      <p className="text-xs text-slate-400">
                        {parseFloat(formData.price).toLocaleString('es-PY')} × 12 − {formData.descuento_anual}% descuento
                      </p>
                    </div>
                  )}
                </>
              )}

              <div className="grid grid-cols-2 gap-4">
                <Field label="Máx. Usuarios">
                  <input required type="number" min="1" className={INPUT_CLS} value={formData.max_users} onChange={e => set('max_users', e.target.value)} />
                </Field>
                <Field label="Máx. Productos">
                  <input required type="number" min="1" className={INPUT_CLS} value={formData.max_products} onChange={e => set('max_products', e.target.value)} />
                </Field>
              </div>

              <Field label="Descripción">
                <textarea
                  rows={2}
                  className={`${INPUT_CLS} resize-none text-slate-500`}
                  value={formData.description}
                  onChange={e => set('description', e.target.value)}
                />
                <p className="text-[10px] text-slate-400 mt-1 px-1">Se genera automáticamente. Podés editarla.</p>
              </Field>

              <div className="flex gap-3 pt-2">
                <button
                  type="button" onClick={() => setIsModalOpen(false)}
                  className="flex-1 bg-slate-100 text-slate-600 font-black uppercase tracking-widest py-3 rounded-2xl hover:bg-slate-200 transition-colors text-xs"
                >
                  Cancelar
                </button>
                <button
                  disabled={loading}
                  className="flex-1 bg-slate-900 text-white font-black uppercase tracking-widest py-3 rounded-2xl text-xs disabled:opacity-50"
                >
en                   {loading ? 'Guardando...' : 'Guardar'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

const INPUT_CLS = 'campo';

